'use client';

import React from 'react';
import { CLASSNAME, formatBmsmCurrency, useGetShippingPolicyByCurrency, type Currency } from '@skin-seoul/service';
import { CheckIcon } from './components/CheckIcon';
import { ConfettiAnimation } from './components/ConfettiAnimation';
import { BMSMGradient } from './components/BMSMGradient';

interface FreeShippingOnlyBannerProps {
  currency: Currency;
  minAmount: number;
  remaining: number;
  applied: boolean;
  /** Progress ratio 0–100 (pre-computed by parent for consistency with multi-tier). */
  progress: number;
  className?: string;
  height?: number;
}

// Deliberately the same value BMSMCampaignBanner emits: this is the
// single-tier branch of the same banner, and a test asserting "the BMSM banner
// rendered" should not have to know which campaign shape is configured.
const BMSM_BANNER_TEST_ID = 'bmsm-banner';

const ICON_SIZE_PX = 24;
const BAR_HEIGHT_PX = 4;

export function FreeShippingOnlyBanner({
  currency,
  minAmount,
  remaining,
  applied,
  progress,
  className = '',
  height = 116,
}: FreeShippingOnlyBannerProps) {
  const { shippingPolicy } = useGetShippingPolicyByCurrency(currency);
  const waivedShippingFee = shippingPolicy?.shippingFee ?? 0;

  const title = applied
    ? "You've unlocked Free Shipping"
    : `Spend ${formatBmsmCurrency(currency, minAmount)} and get Free Shipping`;

  const subtitle = ((): string => {
    if (!applied) return `Add items worth ${formatBmsmCurrency(currency, remaining)} more to claim`;
    if (waivedShippingFee > 0) return `Enjoy ${formatBmsmCurrency(currency, waivedShippingFee)} in savings on this order`;
    return 'Enjoy free shipping on this order';
  })();

  return (
    <div
      data-testid={BMSM_BANNER_TEST_ID}
      className={`relative w-full flex flex-col bg-generic-100 overflow-hidden rounded-2xl ${className}`}
      style={{ minHeight: `${height}px`, height: `${height}px` }}
    >
      <BMSMGradient />

      <div
        className={`z-[1] relative w-full h-full px-4 py-4 flex flex-col justify-between ${CLASSNAME.NOT_TRANSLATE}`}
      >
        <div className="flex flex-col gap-0.5">
          <p
            className="text-label-large font-bold text-generic-0"
            style={{ letterSpacing: '-0.64px' }}
          >
            {title}
          </p>
          <p
            className={`text-label-xsmall font-medium ${applied ? 'text-primary-200' : 'text-[#FFFF27]'}`}
          >
            {subtitle}
          </p>
        </div>

        <div className="relative w-full" style={{ height: `${ICON_SIZE_PX}px` }}>
          <div
            className="absolute left-0 right-0 bg-neutral-600 rounded-sm"
            style={{ top: `${(ICON_SIZE_PX - BAR_HEIGHT_PX) / 2}px`, height: `${BAR_HEIGHT_PX}px` }}
          >
            <div
              className="h-full bg-success-600 rounded-sm transition-[width] duration-300 ease-in-out"
              style={{ width: `${applied ? 100 : progress}%` }}
            />
          </div>

          {applied && (
            <div
              className="absolute"
              style={{
                right: 0,
                top: 0,
                width: `${ICON_SIZE_PX}px`,
                height: `${ICON_SIZE_PX}px`,
              }}
            >
              <CheckIcon achieved variant="check" />
            </div>
          )}
        </div>
      </div>

      <ConfettiAnimation
        showFreeShippingConfetti={applied}
        showDiscountConfetti={false}
      />
    </div>
  );
}
